import { FC, useMemo } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { useLocale } from '../../../../../../../hooks/index';
import {
  MappingCombobox,
  type MappingComboboxOption,
} from '../../../../../templates/components/forms/map-fields-modal/signature-fields-sidebar/mapping-combobox';
import { TemplateFieldType } from '../../../../../templates/enums/mapping-editor.const';
import {
  SIGNATURE_FIELD_TYPE_LABEL,
  SIGNATURE_FIELD_TYPE_OPTIONS,
} from '../../../../../templates/enums/signature-field-type-label.const';
import {
  QuickSignFormSchema,
  QuickSignSignatureTab,
} from '../../../../validation/quick-sign.validation';

type QuickSignSignatureFieldsSidebarProps = {
  control: Control<QuickSignFormSchema>;
  setValue: UseFormSetValue<QuickSignFormSchema>;
};

export const QuickSignSignatureFieldsSidebar: FC<QuickSignSignatureFieldsSidebarProps> = ({
  control,
  setValue,
}) => {
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const tabs = useWatch({
    control,
    name: 'stepFour.tabs',
    defaultValue: [],
  }) as QuickSignSignatureTab[];
  const watchedSignatorySlots = useWatch({ control, name: 'stepThree.signatorySlots' });
  const signatorySlots = useMemo(() => watchedSignatorySlots ?? [], [watchedSignatorySlots]);

  const signerOptions = useMemo((): MappingComboboxOption[] => {
    return signatorySlots
      .map((slot) => slot.stakeholder)
      .filter((stakeholder): stakeholder is NonNullable<typeof stakeholder> => stakeholder != null)
      .map((stakeholder) => ({
        label: stakeholder.fullName,
        value: stakeholder.id,
      }));
  }, [signatorySlots]);

  const completedTabsCount = tabs.filter((tab) => tab.type != null && tab.value != null).length;

  if (!tabs.length) return null;

  return (
    <aside
      className="flex w-[280px] shrink-0 flex-col rounded-xl bg-white"
      style={{
        boxShadow:
          '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
      }}
    >
      <div className="mb-4 flex items-center justify-between px-3 pt-3">
        <span className="text-xs text-gray-500">
          <AppFormattedMessage id="SIGNATURE_FIELDS" />
        </span>
        <span className="text-xs text-[#9747FF]">
          {completedTabsCount}/{tabs.length}
        </span>
      </div>
      <div className="space-y-3 overflow-y-auto px-3 pb-3">
        {tabs.map((tab, index) => (
          <div className="rounded-lg border border-gray-200 bg-white p-3" key={tab.anchorString}>
            <p className="mb-3 text-xs text-[#9747FF]">{tab.anchorString}</p>
            <div className="mt-2">
              <label className="text-xs text-gray-500">
                <AppFormattedMessage id="FIELD_TYPE" />
              </label>
              <MappingCombobox
                onChange={(nextType) => {
                  const updated = [...tabs];
                  updated[index] = {
                    ...updated[index],
                    type: (nextType || undefined) as TemplateFieldType | undefined,
                  };
                  setValue('stepFour.tabs', updated, { shouldValidate: true });
                }}
                options={SIGNATURE_FIELD_TYPE_OPTIONS.map((fieldType) => ({
                  label: messagesLocaleFn(SIGNATURE_FIELD_TYPE_LABEL[fieldType]),
                  value: fieldType,
                }))}
                placeholder={messagesLocale.PLEASE_SELECT}
                value={tab.type ?? ''}
              />
            </div>
            <div className="mt-2">
              <label className="text-xs text-gray-500">
                <AppFormattedMessage id="SIGNER" />
              </label>
              <MappingCombobox
                onChange={(nextSignerId) => {
                  const updated = [...tabs];
                  updated[index] = {
                    ...updated[index],
                    value: nextSignerId || undefined,
                  };
                  setValue('stepFour.tabs', updated, { shouldValidate: true });
                }}
                options={
                  signerOptions.length > 0
                    ? signerOptions
                    : [
                        {
                          disabled: true,
                          label: messagesLocale.NO_RESULTS_FOUND,
                          value: '__not-found__',
                        },
                      ]
                }
                placeholder={messagesLocale.PLEASE_SELECT}
                value={tab.value ?? ''}
              />
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};
